import { useEffect, useId, useRef } from 'react';
import { focusInitialElement, trapTabKey } from './focusTrap';

const WIDTH = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

/**
 * Accessible modal dialog for the Originator redesign.
 * Presentational only — parent owns open state and any submit logic.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocusRef,
  titleClassName = '',
  headerClassName = '',
  footerClassName = '',
}) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const node = dialogRef.current;
    const frame = window.requestAnimationFrame(() => {
      focusInitialElement(node, initialFocusRef);
    });

    function onKey(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      trapTabKey(event, dialogRef.current);
    }

    document.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={() => onCloseRef.current?.()}
        className="absolute inset-0 cursor-default bg-eorig-navy/40 backdrop-blur-[1px]"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        aria-label={title ? undefined : 'Dialog'}
        tabIndex={-1}
        className={`relative z-10 w-full overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card outline-none ${
          WIDTH[size] || WIDTH.md
        }`}
      >
        {title ? (
          <div className={headerClassName || 'border-b border-eorig-border px-5 py-4'}>
            <h2
              id={titleId}
              className={titleClassName || 'text-sm font-semibold text-eorig-ink'}
            >
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-[12px] leading-snug text-eorig-muted">
                {description}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="px-5 py-4">{children}</div>
        {footer ? (
          <div
            className={
              footerClassName ||
              'flex justify-end gap-2 border-t border-eorig-border bg-eorig-surface px-5 py-3'
            }
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
